import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import {
    getGetVariantsInfoUrl,
    getUpdateVariantInfoUrl,
    GetVariantsInfoMethod,
    type GetVariantsInfoSearchParams,
    UpdateVariantInfoMethod,
} from '@/shared/api/openApi';
import { type OrderDto } from '@/shared/api/order';
import { request } from '@/shared/api/request';
import { type VariantsInfo } from '@/shared/api/variants';
import { endUpdateOrderSide, startUpdateOrderSide } from '../slices';
import { type GetVariantsInfoRequestParams } from '../types/GetVariantsInfoRequestParams';
import { type UpdateVariantInfoRequestParams } from '../types/UpdateVariantInfoRequestParams';
import { api } from './api';

const variantsApi = createApi({
    name: 'entities/order/api/variantsApi',
    baseQuery: createBaseQuery(request),
    endpoints: (builder) => {
        return {
            GetVariantsInfo: builder.query<VariantsInfo, GetVariantsInfoRequestParams>({
                query: ({ order_id, store_slug, ...payload }) => {
                    return {
                        url: getGetVariantsInfoUrl({ order_id, store_slug }),
                        method: GetVariantsInfoMethod,
                        params: payload satisfies GetVariantsInfoSearchParams,
                    };
                },
            }),
            UpdateVariantInfo: builder.mutation<OrderDto, UpdateVariantInfoRequestParams>({
                query: (payload) => {
                    return {
                        url: getUpdateVariantInfoUrl(payload),
                        method: UpdateVariantInfoMethod,
                        params: {
                            signature: payload.signature,
                        },
                        body: {
                            delivery_chunk_id: payload.delivery_chunk_id,
                            original_variant_id: payload.original_variant_id,
                            new_variant_id: payload.new_variant_id,
                        },
                    };
                },
                onQueryStarted: async (_, { queryFulfilled }) => {
                    try {
                        startUpdateOrderSide();
                        await queryFulfilled;
                    } finally {
                        endUpdateOrderSide();
                    }
                },
                transformResponse: async (response: OrderDto, _meta, arg) => {
                    api.endpoints.GetOrder.upsertQueryData(
                        {
                            store_slug: arg.store_slug,
                            order_id: arg.order_id,
                            signature: arg.signature,
                        },
                        response
                    );

                    return response;
                },
            }),
        };
    },
});

export const getOrderVariantsInfo = variantsApi.endpoints.GetVariantsInfo.query;

export const updateOrderVariantInfo = variantsApi.endpoints.UpdateVariantInfo.mutate;
